import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { studioApi } from '../lib/api';
import { toast } from 'sonner';
import { Wallet, Plus, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const TIPI = ['Acconto', 'SAL 1', 'SAL 2', 'SAL 3', 'Saldo', 'Altro'];
const STATI = ['Da emettere', 'Emessa', 'Pagata', 'Parziale', 'Scaduta'];
const STATO_COLORS = { 'Da emettere': 'bg-gray-100 text-gray-700', Emessa: 'bg-blue-100 text-blue-800', Pagata: 'bg-emerald-100 text-emerald-800', Parziale: 'bg-yellow-100 text-yellow-800', Scaduta: 'bg-red-100 text-red-800' };
const fmtEur = (v) => `€ ${(v || 0).toLocaleString('it-IT')}`;
const empty = { commessa_id: '', data: '', descrizione: '', importo: 0, tipo: 'Acconto', stato: 'Da emettere', percentuale_su_parcella: 0 };

export default function StudioPagamentiPage() {
  const [items, setItems] = useState([]);
  const [commesse, setCommesse] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const [p, c] = await Promise.all([studioApi.getPagamenti(), studioApi.getCommesse()]);
      setItems(p.data); setCommesse(c.data);
    } catch { toast.error('Errore'); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(empty); setEditingId(null); setOpen(true); };
  const openEdit = (p) => { setForm({ ...empty, ...p }); setEditingId(p.id); setOpen(true); };
  const save = async () => {
    if (!form.commessa_id || !form.descrizione) { toast.error('Commessa e descrizione obbligatori'); return; }
    const payload = { ...form, importo: parseFloat(form.importo) || 0, percentuale_su_parcella: parseFloat(form.percentuale_su_parcella) || 0 };
    try {
      if (editingId) { await studioApi.updatePagamento(editingId, payload); toast.success('Aggiornato'); }
      else { await studioApi.createPagamento(payload); toast.success('Creato'); }
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };
  const remove = async (p) => {
    if (!window.confirm('Eliminare pagamento?')) return;
    try { await studioApi.deletePagamento(p.id); toast.success('Eliminato'); load(); } catch { toast.error('Errore'); }
  };

  const totIncassato = items.filter(p => p.stato === 'Pagata').reduce((s, p) => s + (p.importo || 0), 0);
  const totDaIncassare = items.filter(p => p.stato !== 'Pagata').reduce((s, p) => s + (p.importo || 0), 0);

  return (
    <div className="space-y-4 p-4 lg:p-6" data-testid="studio-pagamenti-page">
      <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
        <div>
          <div className="flex items-center gap-2"><Wallet className="w-6 h-6 text-indigo-600" /><h1 className="text-2xl font-bold">Pagamenti</h1></div>
          <p className="text-sm text-gray-600 mt-1">
            Incassato: <strong className="text-emerald-700">{fmtEur(totIncassato)}</strong> · Da incassare: <strong className="text-amber-700">{fmtEur(totDaIncassare)}</strong>
          </p>
        </div>
        <Button onClick={openCreate} data-testid="new-pagamento-btn"><Plus className="w-4 h-4 mr-1" /> Nuovo pagamento</Button>
      </motion.div>

      {loading ? <p className="text-gray-500 italic">Caricamento...</p> : (
        <div className="bg-white border rounded-lg overflow-hidden shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-600 uppercase"><tr>
              <th className="text-left p-2.5">Data</th><th className="text-left p-2.5">Commessa</th><th className="text-left p-2.5">Cliente</th>
              <th className="text-left p-2.5">Descrizione</th><th className="text-left p-2.5">Tipo</th>
              <th className="text-right p-2.5">Importo</th><th className="text-left p-2.5">Stato</th><th></th>
            </tr></thead>
            <tbody>
              {items.map(p => (
                <tr key={p.id} className="border-t hover:bg-gray-50">
                  <td className="p-2.5 text-xs">{p.data || '-'}</td>
                  <td className="p-2.5 font-mono text-xs text-indigo-700">{p.commessa_codice || '-'}</td>
                  <td className="p-2.5">{p.cliente_nome || '-'}</td>
                  <td className="p-2.5">{p.descrizione}</td>
                  <td className="p-2.5"><Badge variant="outline" className="text-xs">{p.tipo}</Badge></td>
                  <td className="p-2.5 text-right font-mono">{fmtEur(p.importo)}</td>
                  <td className="p-2.5"><Badge className={`text-xs ${STATO_COLORS[p.stato] || ''}`}>{p.stato}</Badge></td>
                  <td className="p-2.5 text-right">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(p)}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(p)}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan="8" className="text-center text-gray-500 italic py-10">Nessun pagamento.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>{editingId ? 'Modifica pagamento' : 'Nuovo pagamento'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2"><Label>Commessa *</Label>
              <Select value={form.commessa_id || '__none__'} onValueChange={(v) => setForm({ ...form, commessa_id: v === '__none__' ? '' : v })}>
                <SelectTrigger><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                <SelectContent><SelectItem value="__none__">--</SelectItem>{commesse.map(c => <SelectItem key={c.id} value={c.id}>{c.codice} · {c.cliente_nome}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Data</Label><Input type="date" value={form.data || ''} onChange={(e) => setForm({ ...form, data: e.target.value })} /></div>
            <div><Label>Tipo</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{TIPI.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2"><Label>Descrizione *</Label><Input value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} /></div>
            <div><Label>Importo (€)</Label><Input type="number" value={form.importo} onChange={(e) => setForm({ ...form, importo: e.target.value })} /></div>
            <div><Label>% su parcella</Label><Input type="number" min="0" max="1" step="0.05" value={form.percentuale_su_parcella} onChange={(e) => setForm({ ...form, percentuale_su_parcella: e.target.value })} /></div>
            <div className="md:col-span-2"><Label>Stato</Label>
              <Select value={form.stato} onValueChange={(v) => setForm({ ...form, stato: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{STATI.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save}>Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
